import React from 'react';
import { FiBriefcase, FiCheckCircle, FiClock, FiLayers, FiMapPin, FiTrendingUp, FiUser } from 'react-icons/fi';
import styles from './Stream.module.css';
import * as utils from '../../utils/utils';
import Sidebar from '../Sidebar/Sidebar';

export default class Stream extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			isLoading: true,
			isLoadingMore: false,
			errorText: '',
			streamLiveResumes: [],
			offset: 0,
			limit: 5,
			hasMore: false,
		};
	}

	componentDidMount() {
		if (!utils.isLoggedIn()) {
			window.location.href = '/signin';
			return;
		}

		this.loadStreamData();
	}

	loadStreamData = () => {
		utils.sendRequest('/stream/getStream', { offset: 0 })
			.then((streamResponse) => {
				const streamLiveResumes = streamResponse.streamLiveResumes || [];
				const limit = streamResponse.limit || 5;

				this.setState({
					isLoading: false,
					errorText: '',
					streamLiveResumes,
					offset: streamLiveResumes.length,
					limit,
					hasMore: streamLiveResumes.length === limit,
				});
			})
			.catch((error) => {
				console.log('stream load error', error);
				this.setState({
					isLoading: false,
					errorText: 'Could not load stream right now. Please try again shortly.',
				});
			});
	};

	loadMoreUpdates = () => {
		const {
			offset,
			limit,
			isLoadingMore,
			streamLiveResumes,
		} = this.state;

		if (isLoadingMore) {
			return;
		}

		this.setState({ isLoadingMore: true }, () => {
			utils.sendRequest('/stream/getStream', { offset })
				.then((responseData) => {
					const nextItems = responseData.streamLiveResumes || [];
					const nextLimit = responseData.limit || limit;

					this.setState({
						isLoadingMore: false,
						streamLiveResumes: [...streamLiveResumes, ...nextItems],
						offset: offset + nextItems.length,
						limit: nextLimit,
						hasMore: nextItems.length === nextLimit,
					});
				})
				.catch((error) => {
					console.log('load more stream updates error', error);
					this.setState({ isLoadingMore: false });
				});
		});
	};

	onNewLiveResumeCheckInClick = () => {
		window.location.href = '/live-resume';
	};

	formatDate = (value) => {
		if (!value) {
			return 'Unknown';
		}

		const date = new Date(value);
		if (Number.isNaN(date.getTime())) {
			return 'Unknown';
		}

		return date.toLocaleDateString(undefined, {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		});
	};

	getChipNames = (items, max = 4) => {
		if (!items || items.length === 0) {
			return [];
		}

		return items.slice(0, max).map((item) => item.name).filter(Boolean);
	};

	renderUpdateCard = (item) => {
		const skillNames = this.getChipNames(item.skills);
		const toolNames = this.getChipNames(item.tools);
		const domainNames = this.getChipNames(item.domains, 3);
		const softSkillNames = this.getChipNames(item.softSkills, 3);
		const personName = `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'Professional Member';

		return (
			<article key={item.userEntityLiveResumeId} className={styles.updateCard}>
				<div className={styles.updateTopRow}>
					<div className={styles.updateMetaGroup}>
						<span className={styles.metaChip}><FiUser /> {personName}</span>
						<span className={styles.metaChip}><FiBriefcase /> {item.role || 'Role Not Set'}</span>
						<span className={styles.metaChip}><FiMapPin /> {item.location || 'Remote'}</span>
						<span className={styles.metaChip}><FiClock /> {this.formatDate(item.createdAt)}</span>
					</div>
					<span className={styles.scoreChip}><FiCheckCircle /> AI Score {item.aiScore || 0}</span>
				</div>

				<p className={styles.liveResumeText}>{item.liveResumeText || 'No live resume update text available.'}</p>

				{(domainNames.length > 0 || skillNames.length > 0 || toolNames.length > 0 || softSkillNames.length > 0) && (
					<div className={styles.tagWrapperGrid}>
						{domainNames.length > 0 && (
							<div className={styles.tagBlock}>
								<h4 className={styles.tagHeading}>Domains</h4>
								<div className={styles.tagRow}>
									{domainNames.map((name) => (
										<span key={`domain-${item.userEntityLiveResumeId}-${name}`} className={styles.tag}>{name}</span>
									))}
								</div>
							</div>
						)}

						{skillNames.length > 0 && (
							<div className={styles.tagBlock}>
								<h4 className={styles.tagHeading}>Skills</h4>
								<div className={styles.tagRow}>
									{skillNames.map((name) => (
										<span key={`skill-${item.userEntityLiveResumeId}-${name}`} className={styles.tag}>{name}</span>
									))}
								</div>
							</div>
						)}

						{toolNames.length > 0 && (
							<div className={styles.tagBlock}>
								<h4 className={styles.tagHeading}>Tools</h4>
								<div className={styles.tagRow}>
									{toolNames.map((name) => (
										<span key={`tool-${item.userEntityLiveResumeId}-${name}`} className={styles.tag}>{name}</span>
									))}
								</div>
							</div>
						)}

						{softSkillNames.length > 0 && (
							<div className={styles.tagBlock}>
								<h4 className={styles.tagHeading}>Soft Skills</h4>
								<div className={styles.tagRow}>
									{softSkillNames.map((name) => (
										<span key={`soft-skill-${item.userEntityLiveResumeId}-${name}`} className={styles.tag}>{name}</span>
									))}
								</div>
							</div>
						)}
					</div>
				)}
			</article>
		);
	};

	render() {
		const {
			isLoading,
			isLoadingMore,
			errorText,
			streamLiveResumes,
			hasMore,
		} = this.state;

		return (
			<div className={styles.pageContainer}>
				<Sidebar />

				<div className={styles.glowTop} />
				<div className={styles.glowBottom} />

				<div className={styles.layoutShell}>
					<section className={styles.leftPanel}>
						{/* <div className={styles.brandBadge}>HQVibe</div> */}

						<div className={styles.profileBlock}>
							<h1 className={styles.title}>Stream</h1>
							<p className={styles.subtitle}>{utils.getUserEmail() || 'Live community feed'}</p>
						</div>

						<div className={styles.metricsGrid}>
							<div className={styles.metricCard}>
								<span className={styles.metricLabel}><FiLayers /> Live Entries</span>
								<strong className={styles.metricValue}>{streamLiveResumes.length}</strong>
							</div>
							<div className={styles.metricCard}>
								<span className={styles.metricLabel}><FiTrendingUp /> Feed Scope</span>
								<strong className={styles.metricValue}>All Users</strong>
							</div>
						</div>

						<button className={styles.primaryButton} onClick={this.onNewLiveResumeCheckInClick}>
							New Check-in <span className={styles.btnArrow}>→</span>
						</button>
					</section>

					<section className={styles.cardPanel}>
						<div className={styles.sectionHeader}>
							<h2 className={styles.formTitle}>Live Resume Streams</h2>
							<p className={styles.formText}>Recent verified live-resume updates across the network.</p>
						</div>

						{errorText !== '' && <div className={styles.errorBox}>{errorText}</div>}
						{isLoading && <div className={styles.loadingBox}>Synchronizing stream indices...</div>}

						{!isLoading && (
							<section className={styles.contentSection}>
								{streamLiveResumes.length === 0 && (
									<div className={styles.emptyBox}>Stream empty. New entries will appear here.</div>
								)}

								<div className={styles.updatesList}>
									{streamLiveResumes.map(this.renderUpdateCard)}
								</div>

								{hasMore && (
									<button
										className={styles.loadMoreButton}
										onClick={this.loadMoreUpdates}
										disabled={isLoadingMore}
									>
										{isLoadingMore ? 'Reading Data...' : 'Fetch Older Entries'}
									</button>
								)}
							</section>
						)}
					</section>
				</div>
			</div>
		);
	}
}
